import Modal from "components/modal";
import { toast, useToast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ScaleLoader } from "react-spinners";
import useMicSettings from "zustand-store/mic";
import TypewriterMarkdown from "components/typewriter";
import useCreateNotes from "zustand-store/create-notes";
import { createPatientSession, updatePatientSession } from "api/patient-session";
import RegeneratedNotes from "sections/modal/regenerated-notes";
import { ReactComponent as MicIcon } from "assets/svgs/mic.svg";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ScaleBarrierLoader from "components/scale-barrier-loader";
import NotesPanel from "./notes-panel";
import PanelTabs from "components/ui/panel-tabs";
import NsLoader from "components/ui/ns-loader";

import { ReactComponent as NextIcon } from "assets/svgs/next.svg";
import { ReactComponent as StarsIcon } from "assets/svgs/stars.svg";
import { ReactComponent as SendAiIcon } from "assets/svgs/send-Ai.svg";
import CloseButton from "components/ui/close-button";
import ChoosePlan from "sections/subscription/choose-plan";
import {
  getPatientNameFromTranscibedText, getRegeneratedNotes, getTranscribedText, fillDocuments,
  getNotesWithMoreTemplates,
  getRecommendation
} from "api/deepgram";
import AddMoreTemplates from "sections/create-notes/add-more-templates";
import { getAllTemplatesUserIdSortByProfession, getAllTemplates, getAllCustomTemplates } from "api/templates";
import "tippy.js/dist/tippy.css";
import useWaveform from "utils/hooks/use-waveform";
import { getUserByCogId } from "api/user";
import TurndownService from 'turndown';
import { Mic, Clock4, NotepadText, Upload, Download, Trash2, StarIcon, ChevronDown, Check, SquareUser, CheckCircle2, AlertTriangle, Languages, Lock, Info } from "lucide-react";
import SearchablePatientDropdown from "components/searchable-patient-dropdown";
import { useForm } from "react-hook-form";
import { getAllPatients } from "api/patient";
import { getUsageData } from "api/usage";
import { getSubscriptionData } from "api/subscriptions";
import { remark } from 'remark';
import remarkHtml from 'remark-html';
import { ReactComponent as CheckFilled } from "assets/svgs/check-filled.svg";
import { ReactComponent as CheckUnfilled } from "assets/svgs/check-unfilled.svg";
import { createFile, downloadFile, getFiles } from 'api/file';
import DeleteFileConfirmation from "sections/modal/DeleteFileConfirmation";
import ReactDOM from "react-dom";
import { deleteFavoriteTemplate, getAllFavoriteTemplates } from "api/favorite";
import errorCopy from "utils/error-copy";
import { applySavedTemplateOrder, saveTemplateOrder } from "utils/methods/templateOrder";
import { DEFAULT_TEMPLATE_IDs, MODES, WORKLETCODE_PLACEHOLDER, LANGUAGE_OPTIONS, UPGRADE_PROMPT_SESSION_MILESTONES } from "enums";
import Spinner from "components/Spinner";
import { useGlobalAudioRecorder } from "zustand-store/global-recorder";
import ReactMarkdown from 'react-markdown';
import WarnIcon from "assets/svgs/warn.jpg";
import useComponentClickOutside from "utils/hooks/use-component-click-outside";
import { TranscriptContainer } from "components/TranscriptContainer";
import GenericError from "components/GenericError";

/* Clears the floating Feedback button (fixed bottom-6, ~40px tall) plus the
   panel row's own bottom margin. */
const PANELS_BOTTOM_RESERVE = 88;

/* Join two transcript segments, lowercasing the start of `next` when `prev`
   does not end with terminal punctuation — the cleanup model capitalises each
   turn's first word, but turns are often mid-sentence continuations (Deepgram
   cuts on 1500ms pauses). Exception: keep "I" uppercase when it is the
   standalone pronoun ("I ", "I'"). */
const joinSegments = (prev, next) => {
  const prevTrimmed = prev.trimEnd();
  if (/[.!?]$/.test(prevTrimmed)) return prevTrimmed + " " + next;
  const isIPronoun = /^I[\s'"]/.test(next) || next === "I";
  const adjusted = isIPronoun ? next : next.charAt(0).toLowerCase() + next.slice(1);
  return prevTrimmed + " " + adjusted;
};

/* A transcript block's text is derived from its segments, never accumulated by
   appending. Blocks restored from storage or typed by hand have no segments —
   they are a single segment covering the whole block. */
const segmentsOf = (block) =>
  block.segments && block.segments.length
    ? block.segments
    : [{ start_time: block.start_time ?? 0, text: block.text ?? "" }];

const stitchSegments = (segments) =>
  [...segments]
    .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0))
    .reduce((acc, seg, i) => (i === 0 ? seg.text : joinSegments(acc, seg.text)), "");

const PANELS_MIN_HEIGHT = 420;

const WS_URL = `${process.env.REACT_APP_DEEPGRAM_WS_URL}/ws/live_transcribe`;

const workletCode = WORKLETCODE_PLACEHOLDER;

const SaveSession = () => {
  const { inputDevice } = useMicSettings();
  const {
    isPaused,
    isStopped,
    stopRecording,
    startRecording,
    analyser,
    isRecording,
    canvasRef,
    audioBlob,
    setAudioBlob,
    setIsRecording,
    setTelehealthData,
    telehealthWS
  } = useGlobalAudioRecorder();

  /* Mirrored-spectrum visualiser, to the owner's Claude Design spec. The hook
     sizes its own FFT buffer off the analyser, so `dataArray` (128 bins, built
     for the old square-grid renderer) is no longer passed. */
  useWaveform({ canvasRef, analyser, isRecording });

  const {
    changesMade,
    currentSession,
    currentPatient,
    generatedNotes,
    setChangesMade,
    transcribedText,
    currentAudioBlob,
    selectedTemplates,
    setSelectedTemplates,
    setGeneratedNotes,
    resetSessionStates,
    setTranscribedText,
    setCurrentAudioBlob,
    setCurrentPatient,
    setCurrentSession,
    showCoreBox,
    setShowCoreBox,
    timerTick,
    setTimerTick,
    shouldShowSaveSession,
    startTimer,
    stopTimer,
    appendTranscription,
    hasInitialAutoSaved,
    setHasInitialAutoSaved,
    hasRecommendationBeenClickedOnce,
    setHasRecommendationBeenClickedOnce,
    recommendation,
    setRecommendation,
    modeCliked,
    setModeCliked,
    sessionStatus,
    setSessionStatus,
    sessionRestoreKey,
    setIsGeneratingNotes,
  } = useCreateNotes();

  const [isEditingNote, setIsEditingNote] = useState(false);
  const notesRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [createSessionLoading, setCreateSessionLoading] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const [savingSession, setSavingSession] = useState(false);
  const [retryingTemplateId, setRetryingTemplateId] = useState(null);
  const [active, setActive] = useState((selectedTemplates || [])[0]);
  const [noteChangesInput, setNoteChangesInput] = useState("");
  const [reUploadText, setReuploadText] = useState(null);
  const [regeneratedNotes, setRegeneratedNotes] = useState(null);
  const [regeneratingNotes, setRegeneratingNotes] = useState(false);
  const [openEditNotesEditor, setOpenNotesEditor] = useState(false);
  const [isOverflowOfTemplateTabs, setIsOverflowOfTemplateTabs] = useState(false);
  const [editorVal, setEditorVal] = useState('');
  const [isNoteEdited, setIsNoteEdited] = useState(false);
  const [showChooseDocumentsModal, setShowChooseDocumentsModal] = useState(false);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(null);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [mobilePanelView, setMobilePanelView] = useState('transcript'); // 'transcript' | 'notes'
  const [mobileTemplateOpen, setMobileTemplateOpen] = useState(false);
  const [isNotesAutoSaving, setIsNotesAutoSaving] = useState(false);
  const [isTranscriptEdited, setIsTranscriptEdited] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [notesBlockReason, setNotesBlockReason] = useState(null); // "short_session" | "short_transcript"
  const [shortTranscriptWarning, setShortTranscriptWarning] = useState(false);

  const [showNewSessionReady, setShowNewSessionReady] = useState(false);

  // Auto-save & crash recovery
  const [resumeBanner, setResumeBanner] = useState(null); // null | recovered session data
  const [showResumingBanner, setShowResumingBanner] = useState(false);
  const [resumingPatientName, setResumingPatientName] = useState('');
  const autoSaveFailCountRef = useRef(0);
  const transcriptDebounceRef = useRef(null);
  const backendAutoSaveIntervalRef = useRef(null);

  const LS_KEY = 'nirvascribe_active_session';

  const writeSessionToLocalStorage = useCallback((overrides = {}) => {
    try {
      const state = useCreateNotes.getState();
      const entry = {
        sessionId: state.currentSession?.id ?? overrides.sessionId,
        patientName: state.currentPatient?.firstName ?? overrides.patientName,
        timerTick: state.timerTick ?? overrides.timerTick ?? 0,
        transcribedText: editableTranscriptionRef.current?.trim() || state.transcribedText,
        generatedNotes: state.generatedNotes ?? [],
        selectedTemplates: state.selectedTemplates ?? [],
        recommendation: state.recommendation ?? '',
        sessionSource: state.sessionSource ?? 'new',
        timestamp: Date.now(),
        ...overrides,
      };
      if (!entry.sessionId) return;
      localStorage.setItem(LS_KEY, JSON.stringify(entry));
    } catch (_) {}
  }, []);

  const clearSessionFromLocalStorage = useCallback(() => {
    try { localStorage.removeItem(LS_KEY); } catch (_) {}
  }, []);

  // On mount: check for a saved session — differentiate crash recovery vs history-continue
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved?.sessionId) return;
      const ageMs = Date.now() - (saved.timestamp || 0);
      if (ageMs > 24 * 60 * 60 * 1000) {
        clearSessionFromLocalStorage();
        return;
      }

      if (saved.sessionSource === 'history_continue') {
        // Silently auto-restore — this was a deliberate history continuation, not a crash
        setCurrentSession({ id: saved.sessionId });
        setCurrentPatient({ firstName: saved.patientName || 'Untitled' });
        setTimerTick(saved.timerTick || 0);
        setTranscribedText(saved.transcribedText || '');
        editableTranscriptionRef.current = saved.transcribedText || '';
        const parsedLS = parseStructuredTranscript(saved.transcribedText);
        const utterancesLS = parsedLS ?? plainTextToUtterances(saved.transcribedText);
        liveUtterancesRef.current = utterancesLS;
        setLiveUtterances(utterancesLS);
        if ((saved.generatedNotes || []).length > 0) {
          setGeneratedNotes(saved.generatedNotes);
          setShowCoreBox();
          const firstTemplateId = saved.generatedNotes[0]?.templateId;
          if (firstTemplateId) setActive(parseInt(firstTemplateId));
        }
        if ((saved.selectedTemplates || []).length > 0) {
          setSelectedTemplates(saved.selectedTemplates);
        }
        if (saved.recommendation) setRecommendation(saved.recommendation);
        setSessionStatus('in_progress');
        setChangesMade(true);
        setResumingPatientName(saved.patientName || '');
        setShowResumingBanner(true);
        setTimeout(() => setShowResumingBanner(false), 5000);
      } else {
        // If a session is already live in Zustand, this is not a crash — the component
        // just remounted (e.g. user opened/closed the history panel). Skip the banner.
        if (useCreateNotes.getState().sessionStatus === 'in_progress') return;
        // Genuine crash recovery — ask the user
        setResumeBanner(saved);
      }
    } catch (_) {}
  }, []);

  const handleResumeSession = useCallback(() => {
    if (!resumeBanner) return;
    setCurrentSession({ id: resumeBanner.sessionId });
    setCurrentPatient({ firstName: resumeBanner.patientName || 'Untitled' });
    setTimerTick(resumeBanner.timerTick || 0);
    setTranscribedText(resumeBanner.transcribedText || '');
    editableTranscriptionRef.current = resumeBanner.transcribedText || '';
    const parsedResume = parseStructuredTranscript(resumeBanner.transcribedText);
    const utterancesResume = parsedResume ?? plainTextToUtterances(resumeBanner.transcribedText);
    liveUtterancesRef.current = utterancesResume;
    setLiveUtterances(utterancesResume);
    if ((resumeBanner.generatedNotes || []).length > 0) {
      setGeneratedNotes(resumeBanner.generatedNotes);
      setShowCoreBox();
      const firstTemplateId = resumeBanner.generatedNotes[0]?.templateId;
      if (firstTemplateId) setActive(parseInt(firstTemplateId));
    }
    if ((resumeBanner.selectedTemplates || []).length > 0) {
      setSelectedTemplates(resumeBanner.selectedTemplates);
    }
    if (resumeBanner.recommendation) {
      setRecommendation(resumeBanner.recommendation);
    }
    setSessionStatus('in_progress');
    setChangesMade(true);
    setResumeBanner(null);
  }, [resumeBanner, setCurrentSession, setCurrentPatient, setTimerTick, setTranscribedText,
      setGeneratedNotes, setShowCoreBox, setSelectedTemplates, setRecommendation,
      setSessionStatus, setChangesMade]);

  const handleDismissResume = useCallback(() => {
    clearSessionFromLocalStorage();
    setResumeBanner(null);
  }, [clearSessionFromLocalStorage]);


  useEffect(() => {
    if (isRecording) startTimer();
    else stopTimer();
  }, [isRecording]);

  const audioContextRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const workletNodeRef = useRef(null);
  const recordingStartTickRef = useRef(0); // timerTick captured at each recording start; offsets new Deepgram timestamps
  const scrollRef = useRef(null);
  const typedNotesRef = useRef(new Set());
  const typedRecommendationRef = useRef(new Set());
  const editableTranscriptionRef = useRef(transcribedText);
  const transcriptScrollRef = useRef(null);
  const micStreamRef = useRef(null);
  const processorRef = useRef(null);
  const isMicStartingRef = useRef(false);

  // Ambient transcription state — utterance objects replace the flat string during live recording
  const liveUtterancesRef = useRef([]);                  // sync ref for stop() / handleWSMessage
  const [liveUtterances, setLiveUtterances] = useState([]);

  async function setupAudioProcessing(stream, ws) {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000,
      });
      audioContextRef.current = audioContext;

      const blob = new Blob([workletCode], { type: "application/javascript" });
      const workletUrl = URL.createObjectURL(blob);
      await audioContext.audioWorklet.addModule(workletUrl);

      const source = audioContext.createMediaStreamSource(stream);

      const workletNode = new AudioWorkletNode(audioContext, "pcm-processor");
      workletNodeRef.current = workletNode;

      workletNode.port.onmessage = (event) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(event.data);
        }
      };

      source.connect(workletNode);
      workletNode.connect(audioContext.destination);

    } catch (e) {
      toast.error("Audio processing failed. Please refresh and try again.");
      stop();
    }
  }

  function getCognitoIdToken() {
    const key = Object.keys(localStorage).find(
      k =>
        k.startsWith("CognitoIdentityServiceProvider.") &&
        k.endsWith(".idToken")
    );

    return key ? localStorage.getItem(key) : null;
  }

  async function start() {
    if (wsRef.current) return;
    if (audioContextRef.current) return;
    if (isMicStartingRef.current) return;
    isMicStartingRef.current = true;

    setIsTranscriptEdited(false);
    setNotesBlockReason(null);
    setShortTranscriptWarning(false);

    // If the user manually typed or pasted text into the transcript area, liveUtterances
    // will be empty even though editableTranscriptionRef has content. Sync them now so
    // the existing text is preserved and new recording appends to it rather than replacing it.
    if (liveUtterancesRef.current.length === 0) {
      const existingText = editableTranscriptionRef.current || useCreateNotes.getState().transcribedText;
      if (existingText && existingText.trim()) {
        const parsed = parseStructuredTranscript(existingText);
        const utterances = parsed ?? plainTextToUtterances(existingText);
        liveUtterancesRef.current = utterances;
        setLiveUtterances(utterances);
      }
    }

    // Capture timerTick at this moment so new Deepgram timestamps (which always start
    // from 0) are offset to continue after any existing transcript content.
    // Special case: when timerTick is 0 (fresh/pasted session) but utterances are
    // already loaded, offset past the last utterance + 31 s so the first recorded
    // block is NOT swallowed by the 30 s merge window.
    const currentTick = useCreateNotes.getState().timerTick;
    if (currentTick === 0 && liveUtterancesRef.current.length > 0) {
      const maxT = Math.max(...liveUtterancesRef.current.map(u => u.start_time));
      recordingStartTickRef.current = maxT + 31;
    } else {
      recordingStartTickRef.current = currentTick;
    }
    setIsRecording(true);
    // Do NOT clear liveUtterances here — handleExitSession() clears them at session
    // end, so a fresh session starts empty, while a continued/resumed session keeps
    // its existing transcript which new utterances will append to.

    const idToken = getCognitoIdToken();

    try {
      if (modeCliked === 0) {
        await setUpInPersonMode(idToken);
      } else {
        await setUpTelehealthMode(idToken);
      }
    } catch (err) {
      if (err.name === 'NotFoundError') {
        useGlobalAudioRecorder.getState().setDeviceId(null);
        toast.error("Selected microphone not found. Please reselect your audio device and try again.");
      } else if (err.name === 'NotAllowedError') {
        toast.error("Microphone access was denied. Please allow microphone access in your browser and try again.");
      } else {
        toast.error("Could not start recording. Please check your microphone and try again.");
      }
      stop();
    } finally {
      isMicStartingRef.current = false;
    }
  }

  async function setUpInPersonMode(idToken) {
    await startRecording();

    const ws = new WebSocket(`${WS_URL}?token=${idToken}&languages=${selectedLanguages.join(",")}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      setupAudioProcessing(
        useGlobalAudioRecorder.getState().stream,
        ws
      );
    };

    ws.onmessage = handleWSMessage;
  }

  let teleConsent = false;

  async function setUpTelehealthMode(idToken) {
    setIsTranscriptEdited(false);
    if (telehealthWS) return;

    let micStream;
    let tabStream;

    try {
      micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      tabStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });
      teleConsent = true;
      setIsRecording(true);
    } catch (err) {
      teleConsent = false;
      setIsRecording(false);
      return;
    }

    const ws = new WebSocket(`${WS_URL}?token=${idToken}&languages=${selectedLanguages.join(",")}`);
    ws.binaryType = "arraybuffer";

    setTelehealthData({
      telehealthWS: ws,
      telehealthMicStream: micStream,
      telehealthScreenStream: tabStream
    });

    ws.onopen = () => {
      setupTelehealthAudioProcessing(micStream, tabStream, ws);
    };

    ws.onmessage = handleWSMessage;

    tabStream.getVideoTracks()[0].onended = stop;
  }


  function setupTelehealthAudioProcessing(micStream, tabStream, ws) {
    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;

    const micSource = audioContext.createMediaStreamSource(micStream);
    const tabSource = audioContext.createMediaStreamSource(tabStream);

    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;

    micSource.connect(processor);
    tabSource.connect(processor);
    processor.connect(audioContext.destination);

    const sourceRate = audioContext.sampleRate;
    const targetRate = 16000;

    processor.onaudioprocess = (e) => {
      if (ws.readyState !== WebSocket.OPEN) return;

      const input = e.inputBuffer.getChannelData(0);
      const ratio = sourceRate / targetRate;
      const len = Math.round(input.length / ratio);
      const pcm = new Int16Array(len);

      for (let i = 0; i < len; i++) {
        let s = input[Math.floor(i * ratio)];
        s = Math.max(-1, Math.min(1, s));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }

      ws.send(pcm.buffer);
    };
  }

  function handleWSMessage(ev) {
    try {
      const data = JSON.parse(ev.data);

      if (data.type === "interim") {
        const { text, start_time } = data;
        // Offset by recording start tick so interims from this recording are
        // distinguishable from any prior recording's interims.
        const offsetStart = (start_time ?? 0) + recordingStartTickRef.current;
        const current = liveUtterancesRef.current;

        // Find last interim with matching offsetStart (±0.1s) and update in place
        let foundIdx = -1;
        for (let i = current.length - 1; i >= 0; i--) {
          if (current[i].status === "interim" && Math.abs(current[i].start_time - offsetStart) < 0.1) {
            foundIdx = i;
            break;
          }
        }

        const updated = [...current];
        if (foundIdx >= 0) {
          updated[foundIdx] = { ...updated[foundIdx], text };
        } else {
          updated.push({ id: crypto.randomUUID(), status: "interim", text, start_time: offsetStart });
        }
        liveUtterancesRef.current = updated;
        setLiveUtterances([...updated]);

      } else if (data.type === "cleaned_turns") {
        const { turns } = data;

        // Filter empty turns (noise events with no text)
        const validTurns = (turns || []).filter(t => t.text && t.text.trim().length > 0);
        if (validTurns.length === 0) return;

        const current = liveUtterancesRef.current;
        /* Drop pending interims; keep already-cleaned utterances as base, held in
           start_time order. The sort is the invariant everything below depends on:
           blocks arrive out of order (see the insert comment) and every "previous
           block" decision here means the chronologically previous one. */
        const updated = current
          .filter(u => u.status === "cleaned")
          .sort((a, b) => a.start_time - b.start_time);

        // Stitch all turns from this flush into one timestamped block.
        // Offset by recordingStartTickRef so timestamps from a resumed/continued
        // session correctly follow any prior transcript content.
        const stitchedText = validTurns.reduce((acc, turn, i) =>
          i === 0 ? turn.text : joinSegments(acc, turn.text),
        "");
        const startTime = (validTurns[0].start_time ?? 0) + recordingStartTickRef.current;

        /* Order on start_time — NEVER on arrival, and NEVER by concatenating.

           The backend flushes each buffered chunk with `asyncio.create_task`, so
           several LLM cleanup calls are in flight at once and each may retry
           twice on a failed hallucination check. A later chunk routinely comes
           back before an earlier one.

           This used to `push()` in arrival order and merge into
           `updated[updated.length - 1]`. Two things were wrong, and the second
           is the one that corrupted consults:

             - out-of-order blocks were appended out of order, and
             - merging concatenated the new text onto the END of the block's
               text, whatever time it belonged to. Since a whole short consult
               falls inside the 30 s window, every chunk merged into one block
               and the block's text ended up in arrival order permanently. One
               real consult came back as "headache … come back to me … Panadol
               … drink more water".

           Sorting afterwards cannot undo that: once two chunks are concatenated
           the block carries a single start_time and the order inside it is lost.
           So a block keeps its `segments`, each with its own start_time, and its
           text is re-stitched from them in time order every time one arrives.
           Deepgram's start_time is authoritative, so this is correct however late
           a chunk lands.

           The 30 s window still does its original job — stopping long clinician
           monologues, which Deepgram cuts on natural pauses, from fragmenting
           into many small timestamp blocks. */
        const MERGE_WINDOW_S = 30;
        const newSegment = { start_time: startTime, text: stitchedText };

        const followingIdx = updated.findIndex(b => b.start_time > startTime);
        const insertAt = followingIdx === -1 ? updated.length : followingIdx;
        const prev = insertAt > 0 ? updated[insertAt - 1] : null;

        if (prev && (startTime - prev.start_time) < MERGE_WINDOW_S) {
          const segments = [...segmentsOf(prev), newSegment];
          updated[insertAt - 1] = {
            ...prev,
            segments,
            text: stitchSegments(segments),
          };
        } else {
          updated.splice(insertAt, 0, {
            id: crypto.randomUUID(),
            status: "cleaned",
            text: stitchedText,
            start_time: startTime,
            segments: [newSegment],
          });
        }

        liveUtterancesRef.current = updated;
        setLiveUtterances([...updated]);

        // Flush finalized transcript to the editable ref so localStorage captures it
        editableTranscriptionRef.current = assembleTranscriptWithTimestamps(updated);
        writeSessionToLocalStorage();
      }
    } catch { }
  }

  function handleUtteranceTextChange(id, newText) {
    // The edit replaces the whole block, so it collapses to one segment. Keeping
    // the old segments would let a late-arriving chunk re-stitch the block from
    // stale text and silently discard what the clinician just typed.
    const updated = liveUtterancesRef.current.map(u =>
      u.id === id
        ? { ...u, text: newText, segments: [{ start_time: u.start_time ?? 0, text: newText }] }
        : u
    );
    liveUtterancesRef.current = updated;
    setLiveUtterances([...updated]);
    editableTranscriptionRef.current = assembleTranscriptWithTimestamps(updated);
    setIsTranscriptEdited(true);
  }

  function assembleFinalTranscript(utterances) {
    const cleaned = utterances
      .filter(u => u.status === "cleaned")
      .sort((a, b) => a.start_time - b.start_time);

    // Collect any trailing interim text (words spoken right before stop() was pressed
    // that Deepgram hadn't yet flushed as a final result — e.g. a closing "Thanks").
    const trailingInterim = utterances
      .filter(u => u.status === "interim")
      .map(u => u.text)
      .join(" ")
      .trim();

    if (cleaned.length === 0) {
      return trailingInterim || "";
    }

    const cleanedText = cleaned.map(u => u.text).join("\n");
    return trailingInterim ? `${cleanedText}\n${trailingInterim}` : cleanedText;
  }

  // Builds structured text with [t=<seconds>]text markers for DB storage.
  // Allows timestamps to survive save/restore and be parsed back into utterances.
  function assembleTranscriptWithTimestamps(utterances) {
    const cleaned = utterances
      .filter(u => u.status === "cleaned")
      .sort((a, b) => a.start_time - b.start_time);

    const trailingInterim = utterances
      .filter(u => u.status === "interim")
      .map(u => u.text)
      .join(" ")
      .trim();

    if (cleaned.length === 0) return trailingInterim || "";

    // Replace any embedded newlines in utterance text — the format uses \n as a
    // record separator, so internal \n would corrupt parseStructuredTranscript.
    const lines = cleaned.map(u => `[t=${u.start_time ?? 0}]${u.text.replace(/\n/g, ' ')}`).join("\n");
    return trailingInterim ? `${lines}\n${trailingInterim}` : lines;
  }

  // Wraps plain-text (no [t=N] markers) into a single utterance at t=0.
  // One block, one 00:00 timestamp — preserves the text as-is so recording
  // can append to it. The recording offset logic in start() ensures the first
  // recorded block lands beyond the 30 s merge window (at t≥31).
  function plainTextToUtterances(text) {
    if (!text || !text.trim()) return [];
    return [{ id: 'restored-plain-0', status: 'cleaned', text: text.trim(), start_time: 0 }];
  }

  // Parses [t=N]text lines back into a liveUtterances array.
  // Returns null for plain-text (old/unstructured) transcripts.
  // Continuation lines (not starting with [t=) are appended to the previous
  // utterance — this recovers data from sessions stored before the newline-
  // sanitisation fix in assembleTranscriptWithTimestamps.
  function parseStructuredTranscript(text) {
    if (!text || !text.startsWith('[t=')) return null;
    const utterances = [];
    text.split('\n').forEach((line, i) => {
      const match = line.match(/^\[t=([\d.]+)\]([\s\S]*)$/);
      if (match) {
        utterances.push({
          id: `restored-${i}`,
          status: 'cleaned',
          text: match[2],
          start_time: parseFloat(match[1]),
        });
      } else if (utterances.length > 0 && line.trim()) {
        // Continuation line from a multi-line utterance (pre-sanitisation data)
        utterances[utterances.length - 1] = {
          ...utterances[utterances.length - 1],
          text: utterances[utterances.length - 1].text + ' ' + line.trim(),
        };
      }
    });
    return utterances.length > 0 ? utterances : null;
  }

  // Strips [t=N] markers to produce plain text for SOAP generation and word count.
  function stripTimestampMarkers(text) {
    if (!text) return text;
    return text.replace(/^\[t=[\d.]+\]/gm, '').trim();
  }

  function stop() {
    const { telehealthWS, telehealthMicStream, telehealthScreenStream } = useGlobalAudioRecorder.getState();
    telehealthWS?.close();

    wsRef.current?.close();
    wsRef.current = null;

    processorRef.current?.disconnect();
    processorRef.current = null;
    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;

    if (audioContextRef.current) {
      if (audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }

    [telehealthMicStream, telehealthScreenStream, streamRef.current].forEach(s => {
      s?.getTracks().forEach(t => t.stop());
    });

    setTelehealthData({
      telehealthWS: null,
      telehealthMicStream: null,
      telehealthScreenStream: null
    });

    stopRecording();
    setIsRecording(false);

    // Promote any remaining interim utterances to cleaned so they:
    // (a) display as resolved text rather than grey italic, and
    // (b) get included in the SOAP transcript via assembleFinalTranscript.
    const currentUtterances = liveUtterancesRef.current;
    const hasInterims = currentUtterances.some(u => u.status === "interim");
    if (hasInterims) {
      const interimText = currentUtterances
        .filter(u => u.status === "interim")
        .map(u => u.text)
        .join(" ")
        .trim();
      if (interimText) {
        // Sorted, so "the last block" is the chronologically last one. Trailing
        // interim is the final thing that was said; appending it to whichever
        // block happened to land last in the array would bury it mid-transcript.
        const cleanedBlocks = currentUtterances
          .filter(u => u.status === "cleaned")
          .sort((a, b) => a.start_time - b.start_time);
        if (cleanedBlocks.length > 0) {
          cleanedBlocks[cleanedBlocks.length - 1] = {
            ...cleanedBlocks[cleanedBlocks.length - 1],
            text: cleanedBlocks[cleanedBlocks.length - 1].text + " " + interimText,
          };
          liveUtterancesRef.current = cleanedBlocks;
        } else {
          // No cleaned blocks at all — promote interim as a single cleaned block
          const promoted = [{ id: crypto.randomUUID(), status: "cleaned", text: interimText, start_time: 0 }];
          liveUtterancesRef.current = promoted;
        }
        setLiveUtterances([...liveUtterancesRef.current]);
      }
    }

    // Assemble structured transcript (with timestamps) for storage
    const assembled = assembleTranscriptWithTimestamps(liveUtterancesRef.current);
    if (assembled) {
      editableTranscriptionRef.current = assembled;
    }

    if (useCreateNotes.getState().timerTick < 30) {
      // Only hard-block on duration when there is no substantial pre-existing content
      // (e.g. a pasted transcript). If the combined transcript already has ≥ 30 words,
      // fall through — the word-count guard inside uploadAudio handles adequacy.
      const plainForCheck = stripTimestampMarkers(editableTranscriptionRef.current || '');
      const wordCountForCheck = plainForCheck.trim().split(/\s+/).filter(Boolean).length;
      if (wordCountForCheck < 30) {
        setNotesBlockReason("short_session");
        setShowCoreBox(true);
        return;
      }
    }

    uploadAudio();
  }

  // Add this new function right after stop()
  const cleanupAudioResources = async () => {
    const { telehealthWS, telehealthMicStream, telehealthScreenStream } = useGlobalAudioRecorder.getState();
    
    // Close telehealth resources
    if (telehealthWS) {
      telehealthWS.close();
    }
    
    // Stop all telehealth streams
    [telehealthMicStream, telehealthScreenStream].forEach(s => {
      s?.getTracks().forEach(t => t.stop());
    });
    
    // Close websocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    // Disconnect audio worklets
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    
    // Close audio context
    if (audioContextRef.current) {
      if (audioContextRef.current.state !== "closed") {
        await audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }
    
    // Clear telehealth data
    setTelehealthData({
      telehealthWS: null,
      telehealthMicStream: null,
      telehealthScreenStream: null
    });
    
    // Stop any ongoing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };


  const autoScrollEnabledRef = useRef(true);
  const [showScrollButton, setShowScrollButton] = useState(false);

  /* Was a toggle. The panel header is a two-tab control now, so Transcript and
     Insights each set their own state — a toggle left no visible way back from
     Insights: you had to work out that the lit button was a switch and press it
     a second time. */
  const showInsights = async () => {
    setShowRecommendation(true);
    setHasRecommendationBeenClickedOnce(true);

    if (!currentSession) return;
    if (editableTranscriptionRef.current.length === 0 && transcribedText.length === 0) {
      toast.warn("No Transcription Provided");
      return;
    }

    if (recommendation.length === 0) {
      const payload = {
        transcribedText: getLatestTranscript(),
        sessionId: currentSession.id,
      };
      console.log("[insights] payload:", payload);
      setRecommendationLoading(true);
      try {
        const res = await getRecommendation(payload);
        console.log("[insights] response:", res);
        setRecommendation(normalize(res.output));
      } catch (err) {
        console.error("[insights] error:", err?.response?.status, err?.response?.data, err?.message);
        toast.error("Failed to generate insights. Please try again.");
      } finally {
        setRecommendationLoading(false);
      }
    }
  }

  const handleTranscriptUpdate = async () => {
    if (!currentSession) {
      await handleSubmit((data) =>
        handleSessionCreation(data, false))();
      return;
    }

    await handleUpload();
    setIsTranscriptEdited(false);
  };

  useEffect(() => {
    if (!currentSession || !isTranscriptEdited) return;

    async function callAudio() {
      await uploadAudio();
      setIsTranscriptEdited(false);
    }
    callAudio();
  }, [currentSession]);

  useEffect(() => {
    if (!isRecording) return;

    const el = transcriptScrollRef.current;
    if (!el) return;

    if (isRecording && autoScrollEnabledRef.current) {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [liveUtterances, isRecording]);

  // On mobile: switch to transcript panel when recording starts, notes panel when notes arrive
  useEffect(() => {
    if (isRecording) setMobilePanelView('transcript');
  }, [isRecording]);

  useEffect(() => {
    if (generatedNotes?.length > 0) setMobilePanelView('notes');
  }, [generatedNotes]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const handleSessionCreation = async (data, startMic = true) => {
    if (!data.firstName || data.firstName.trim() === "") {
      data.firstName = "Untitled";
    }

    const patientData = {
      firstName: data.firstName,
      lastName: "",
      dateOfBirth: new Date("2000-01-01"),
    };

    let response;
    try {
      setCreateSessionLoading(true);
      response = await createPatientSession(patientData);
    } catch (error) {
      GenericError();
      return;
    } finally {
      setCreateSessionLoading(false);
    }

    setCurrentPatient(patientData);
    setCurrentSession(response);
    setSessionStatus('in_progress');
    setChangesMade(true);
    writeSessionToLocalStorage({ sessionId: response.id, patientName: patientData.firstName, timerTick: 0 });

    if (startMic) {
      await start();
    }
  }

  const { data: currentUser } = useQuery("currentUser", getUserByCogId);
  const userId = currentUser?.cognitoId;

  const { data: userData, isLoading } = useQuery("userProfile", getUserByCogId, {});

  const { data: customTemplatesData = [], isLoading: customTemplatesLoading } = useQuery(
    ['customTemplates'],
    () => getAllCustomTemplates(userData.id),
    {
      enabled: !!userData?.id,
    }
  );

  const {
    data: allTemplates = [],
    isLoading: templatesLoading,
    error: templatesError,
    refetch: refetchTemplates
  } = useQuery(
    ["templates", userId],
    () => {
      if (userId) {
        return getAllTemplatesUserIdSortByProfession(userId);
      } else {
        return getAllTemplates();
      }
    },
    {}
  );

  const checkOverflow = () => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth;
    setIsOverflowOfTemplateTabs(hasOverflow);
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 180;
      scrollRef.current.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth"
      });
    }
  };

  useEffect(() => {
    checkOverflow();
    setActive(selectedTemplates[0]);
  }, [selectedTemplates]);

  useEffect(() => {
    if (!templatesLoading && !customTemplatesLoading) {
      checkOverflow();
    }
  }, [templatesLoading, customTemplatesLoading]);

  useEffect(() => {
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, []);

  const speak = useCallback((text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();

      const removeMarkdown = (text) => {
        return text
          .replace(/\*\*(.*?)\*\*/g, "$1")
          .replace(/\*(.*?)\*/g, "$1")
          .replace(/\[(.*?)\]\(.*?\)/g, "$1")
          .replace(/^#+\s+/gm, "")
          .replace(/`{1,3}(.*?)`{1,3}/g, "$1")
          .replace(/^\s*[-+*]\s+/gm, "")
          .replace(/^\s*\d+\.\s+/gm, "")
          .replace(/\n{2,}/g, "\n")
          .trim();
      };

      const splitIntoSentences = (text) => {
        return text
          .replace(/\.+/g, ".|")
          .replace(/\?/g, "?|")
          .replace(/!/g, "!|")
          .split("|")
          .map((sentence) => sentence.trim())
          .filter((sentence) => sentence.length > 0);
      };

      const cleanText = removeMarkdown(text);
      const sentences = splitIntoSentences(cleanText);
      let currentSentence = 0;

      const speakNextSentence = () => {
        if (currentSentence < sentences.length) {
          const utterance = new SpeechSynthesisUtterance(
            sentences[currentSentence]
          );
          utterance.lang = "en-US";
          utterance.rate = 1;
          utterance.pitch = 1;
          utterance.onend = () => {
            currentSentence++;
            speakNextSentence();
          };
          utterance.onerror = (event) => {
            console.error("SpeechSynthesisUtterance error", event);
            setIsDictating(false);
          };
          window.speechSynthesis.speak(utterance);
        } else {
          setIsDictating(false);
        }
      };

      setIsDictating(true);
      speakNextSentence();
    } else {
      toast.error("Text-to-speech not supported in this browser.");
    }
  }, []);

  const handleDictate = () => {
    if (isDictating) {
      window.speechSynthesis.cancel();
      setIsDictating(false);
    } else {
      const textToRead =
        (generatedNotes || []).find((el) => parseInt(el.templateId) === active)
          ?.output || "No text available to dictate.";
      speak(textToRead);
    }
  };

  const handleNoteChangesInput = (e) => {
    setNoteChangesInput(e.target.value);
  };

  const handleSelectDoc = (doc) => {
    if (selectedDocIds.includes(doc.id)) {
      setSelectedDocIds(prev => prev.filter(currDocId => currDocId !== doc.id));
    }
    else {
      const newDocIds = [...selectedDocIds];
      newDocIds.push(doc.id);
      setSelectedDocIds(newDocIds);
    }
  }

  const handleDownload = async (docId) => {
    const fileData = await downloadFile(docId);
    const link = document.createElement("a");
    link.href = fileData.url;
    link.download = fileData.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const DocItem = ({ doc }) => {
    const isSelected = selectedDocIds.includes(doc.id);

    return (
      <li key={`${doc.id}`} className="border-b border-rule last:border-b-0 transition-all duration-200 hover:bg-shade-50">
        <div
          onClick={() => handleSelectDoc(doc)}
          className="px-4 py-3 flex items-center gap-3 cursor-pointer"
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="flex-shrink-0">
              {isSelected ? <CheckFilled /> : <CheckUnfilled />}
            </div>
            <span className="flex-1 text-sm font-medium text-ink">{doc.fileName}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(doc.id);
              }}
              className="p-2 hover:bg-shade-50 rounded-ns transition-all duration-200"
            >
              <Download size={18} className='text-accent' />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteFileModal({
                  docId: doc.id,
                  docName: doc.fileName,
                });
              }}
              className="p-2 hover:bg-danger/10 rounded-ns transition-all duration-200"
            >
              <Trash2 size={18} className="text-danger" />
            </button>
          </div>
        </div>
      </li>
    );
  };

  const handleSaveSession = async () => {
    if (!currentSession?.id) return;
    if (!generatedNotes || generatedNotes.length === 0) return;
    try {
      setSavingSession(true);
      await updatePatientSession(currentSession?.id, {
        notes: (generatedNotes || []).map((el) => ({
          notesContent: el.output,
          patientSessionId: currentSession?.id,
          templateId: parseInt(el.templateId),
        })),
        duration: timerTick,
        transcribedText: getLatestTranscript(),
        firstName: currentPatient.firstName,
        recommendation: recommendation
      });
      setChangesMade(false);
      writeSessionToLocalStorage();
      autoSaveFailCountRef.current = 0;
    } catch (error) {
      console.error("Error saving session:", error);
      GenericError();
    } finally {
      setSavingSession(false);
    }
  };

  // Silent backend auto-save (only fires when notes exist)
  const autoSaveToBackend = useCallback(async () => {
    const state = useCreateNotes.getState();
    if (!state.currentSession?.id) return;
    if (!state.generatedNotes || state.generatedNotes.length === 0) return;
    if (!state.currentPatient) return;
    try {
      await updatePatientSession(state.currentSession.id, {
        notes: state.generatedNotes.map((el) => ({
          notesContent: el.output,
          patientSessionId: state.currentSession.id,
          templateId: parseInt(el.templateId),
        })),
        duration: state.timerTick,
        transcribedText: editableTranscriptionRef.current?.trim() || state.transcribedText,
        firstName: state.currentPatient.firstName,
        recommendation: state.recommendation,
      });
      writeSessionToLocalStorage();
      autoSaveFailCountRef.current = 0;
    } catch (_) {
      autoSaveFailCountRef.current += 1;
    }
  }, [writeSessionToLocalStorage]);

  // Sync editable ref, utterances, and active tab when a session is restored from history
  useEffect(() => {
    if (sessionRestoreKey === 0) return;
    const state = useCreateNotes.getState();
    editableTranscriptionRef.current = state.transcribedText || '';
    const parsed = parseStructuredTranscript(state.transcribedText);
    const utterances = parsed ?? plainTextToUtterances(state.transcribedText);
    liveUtterancesRef.current = utterances;
    setLiveUtterances(utterances);
    const firstTemplateId = (state.generatedNotes || [])[0]?.templateId;
    if (firstTemplateId) setActive(parseInt(firstTemplateId));
  }, [sessionRestoreKey]);

  // Write to localStorage whenever transcript changes (debounced 5s)
  useEffect(() => {
    if (!currentSession?.id) return;
    clearTimeout(transcriptDebounceRef.current);
    transcriptDebounceRef.current = setTimeout(() => {
      writeSessionToLocalStorage();
    }, 5000);
    return () => clearTimeout(transcriptDebounceRef.current);
  }, [transcribedText, currentSession?.id]);

  // Write to localStorage immediately when notes are generated or updated
  useEffect(() => {
    if (!currentSession?.id) return;
    if (!generatedNotes || generatedNotes.length === 0) return;
    writeSessionToLocalStorage();
  }, [generatedNotes, currentSession?.id]);

  // Mark session in_progress when recording starts; write localStorage immediately
  useEffect(() => {
    if (isRecording && currentSession?.id) {
      setSessionStatus('in_progress');
      writeSessionToLocalStorage();
    }
  }, [isRecording, currentSession?.id]);

  // Periodic localStorage save during recording (backstop for silent gaps between cleaned_turns)
  useEffect(() => {
    if (!isRecording || !currentSession?.id) return;
    const id = setInterval(() => {
      writeSessionToLocalStorage();
    }, 15000);
    return () => clearInterval(id);
  }, [isRecording, currentSession?.id, writeSessionToLocalStorage]);

  // Backend auto-save interval: every 30s when notes exist
  useEffect(() => {
    if (backendAutoSaveIntervalRef.current) {
      clearInterval(backendAutoSaveIntervalRef.current);
      backendAutoSaveIntervalRef.current = null;
    }
    if (showCoreBox && currentSession?.id) {
      backendAutoSaveIntervalRef.current = setInterval(() => {
        autoSaveToBackend();
      }, 30000);
    }
    return () => {
      if (backendAutoSaveIntervalRef.current) {
        clearInterval(backendAutoSaveIntervalRef.current);
        backendAutoSaveIntervalRef.current = null;
      }
    };
  }, [showCoreBox, currentSession?.id, autoSaveToBackend]);

  useEffect(() => {
    if (!showCoreBox || hasInitialAutoSaved) return;

    const callSaveSession = async () => {
      setIsNotesAutoSaving(true);
      await handleSaveSession();
      setIsNotesAutoSaving(false);
      setHasInitialAutoSaved(true);
    };

    callSaveSession();
  }, [showCoreBox]);

  const handleRegenerateNotesOnPressEnterKey = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      if (noteChangesInput.toString().trim().length === 0) return;
      if (!regeneratingNotes) handleRegeneratedNotes();
    }
  };

  const handleRegeneratedNotes = async () => {
    try {
      setRegeneratingNotes(true);
      const foundNote = (generatedNotes || []).find(
        (el) => parseInt(el.templateId) === active
      );
      const payload = {
        templateId: parseInt(active),
        transcribedText: transcribedText,
        userInput: noteChangesInput,
        sessionId: currentSession?.id,
        prevGeneratedNotes: foundNote?.output || "",
      };
      const response = await getRegeneratedNotes(payload);
      setRegeneratedNotes(response);
    } catch (error) {
      console.error("Error regenerating notes:", error);
      toast.error("That didn't generate. Your transcript is safe — try again.");
    } finally {
      setRegeneratingNotes(false);
      setNoteChangesInput("");
    }
  };

  const handleRetryTemplate = async () => {
    try {
      setRetryingTemplateId(active);
      const payload = {
        transcribedText: getLatestTranscript(),
        selectedTemplates: [active],
        sessionId: currentSession?.id,
      };
      const response = await getNotesWithMoreTemplates(payload);
      const [retried] = response.outputs.map(note => ({
        ...note,
        output: normalize(note.output),
      }));
      const updatedNotes = (generatedNotes || []).map(note =>
        parseInt(note.templateId) === parseInt(active) ? retried : note
      );
      setGeneratedNotes(updatedNotes);
      setChangesMade(true);
    } catch (error) {
      console.error("Error retrying template:", error);
      toast.error("That didn't generate. Your transcript is safe — try again.");
    } finally {
      setRetryingTemplateId(null);
    }
  };

  const handleExitSession = () => {
    clearSessionFromLocalStorage();
    resetSessionStates();
    setValue('firstName', '');
    setAudioBlob(null);
    setActive((selectedTemplates || [])[0])
    editableTranscriptionRef.current = "";
    setShowRecommendation(false);
    setRecommendation('');
    setHasRecommendationBeenClickedOnce(false);
    typedRecommendationRef.current = new Set();
    setLiveUtterances([]);
    liveUtterancesRef.current = [];
  };

  const handleNextPatientClick = async () => {
    setSessionStatus('completed');
    clearSessionFromLocalStorage();

    // Capture before save — backend count increments during handleSaveSession,
    // so post-invalidate getQueryData may still return the old value due to timing.
    const prevSessions = userUsageData?.sessions || 0;
    const isFreeTrial = userUsageData?.isFreeTrial;

    await handleSaveSession();
    await queryClient.invalidateQueries("userUsage");

    const newSessionCount = prevSessions + 1;
    if (isFreeTrial && UPGRADE_PROMPT_SESSION_MILESTONES.includes(newSessionCount)) {
      window.dispatchEvent(
        new CustomEvent("nirva-upgrade-milestone", { detail: { milestone: newSessionCount } })
      );
    }

    setSelectedLanguages(["en"]);
    setLangError("");
    setNotesBlockReason(null);
    setShortTranscriptWarning(false);
    handleExitSession();
    setShowNewSessionReady(true);
    setTimeout(() => setShowNewSessionReady(false), 3000);
  };

  const copyToClipboard = async () => {
    try {
      const textToCopy = (generatedNotes || []).find((el) => parseInt(el.templateId) === active)?.output || "";

      if (!textToCopy) {
        toast.error("Nothing to copy yet.");
        return;
      }

      const formatForHTML = (content) => {
        let formatted = content;
        formatted = formatted.replace(/\n\n/g, '\n');
        formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
        formatted = formatted.replace(/\n/g, '<br>');
        formatted = `<p>${formatted}</p>`;
        return formatted;
      };

      const formatForPlainText = (content) => {
        let formatted = content;
        formatted = formatted.replace(/\*\*(.*?)\*\*/g, '$1');
        formatted = formatted.replace(/\*(.*?)\*/g, '$1');
        formatted = formatted.replace(/\n\n/g, '\n');
        formatted = formatted.replace(/[ \t]+\n/g, '\n');
        formatted = formatted.replace(/\n[ \t]+/g, '\n');
        return formatted.trim();
      };

      const htmlContent = formatForHTML(textToCopy);
      const plainContent = formatForPlainText(textToCopy);

      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainContent], { type: 'text/plain' })
      });

      await navigator.clipboard.write([clipboardItem]);
      
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      toast.success("Copied.");

    } catch (err) {
      console.error("Copy to clipboard error:", err);
      try {
        const textToCopy = (generatedNotes || []).find((el) => parseInt(el.templateId) === active)?.output || "";
        let plainContent = textToCopy.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
        plainContent = plainContent.replace(/\n\n/g, '\n');
        plainContent = plainContent.replace(/[ \t]+\n/g, '\n');
        plainContent = plainContent.replace(/\n[ \t]+/g, '\n');
        plainContent = plainContent.trim();

        await navigator.clipboard.writeText(plainContent);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
        toast.success("Copied as plain text.");
      } catch (fallbackErr) {
        console.error("Fallback copy also failed:", fallbackErr);
        toast.error("That didn't copy. Try again.");
      }
    }
  };

  const getLatestTranscript = () =>
    editableTranscriptionRef.current?.trim() || transcribedText;



  const handleUpload = async () => {
    const latestTranscript = getLatestTranscript();
    const plainTranscript = stripTimestampMarkers(latestTranscript);
    const wordCount = plainTranscript.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < 30) {
      setNotesBlockReason("short_transcript");
      return;
    }
    setShortTranscriptWarning(wordCount < 150);

    try {
      setLoading(true);
      setIsGeneratingNotes(true);

      const payload = {
        transcribedText: plainTranscript,
        selectedTemplates: selectedTemplates ?? [],
        sessionId: currentSession?.id,
      };

      const response = await getNotesWithMoreTemplates(payload);

      const responseGeneratedNotes = response.outputs.map(note => ({
        ...note,
        output: normalize(note.output),
      }));
      setNotesBlockReason(null);
      setChangesMade(true);
      setGeneratedNotes(responseGeneratedNotes);
      setTranscribedText(latestTranscript);
      typedNotesRef.current = new Set();
      setRecommendation('');
      typedRecommendationRef.current = new Set();
    } catch (error) {
      console.error("Error uploading audio:", error);
      GenericError();
    } finally {
      setLoading(false);
      setIsGeneratingNotes(false);
    }
  };

  useEffect(() => {
    async function getPatientName() {
      try {
        const payload = {
          transcribedText: transcribedText,
          sessionId: currentSession?.id,
        }

        const name = await getPatientNameFromTranscibedText(payload);
        if (name !== null) {
          setCurrentPatient({
            ...currentPatient,
            firstName: name,
          })
        }
      }
      catch (error) {
        toast.error('Failed to get the patient name');
      }
    }

    if (currentPatient && currentPatient.firstName === 'Untitled' && transcribedText)
      getPatientName();
  }, [transcribedText])

  useEffect(() => {
    return () => {
      setTranscribedText(editableTranscriptionRef.current);
    }
  }, [])

  const currentPatientName = currentPatient?.firstName === 'Untitled' ? '' : currentPatient?.firstName;

  const { data: userUsageData, isLoading: userDataLoading } = useQuery("userUsage", getUsageData);

  const { data: subscriptionData } = useQuery(
    "userSubscription",
    getSubscriptionData,
    {
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const isFreeTrialSessionExhausted =
    userUsageData &&
    userUsageData.isFreeTrial &&
    userUsageData.sessions >=
    (process.env.REACT_APP_TRIAL_USER_LIMIT_SESSIONS || 15) &&
    !currentSession;

  const isUserProTrialInactive =
    userUsageData &&
    !userUsageData.isFreeTrial &&
    subscriptionData &&
    subscriptionData.status !== "active" &&
    subscriptionData.status !== "trialing";

  const currentNote = (generatedNotes || []).find(note => note.templateId === active)?.output;

  const formatDuration = (durationInSeconds) => {
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = Math.floor(durationInSeconds % 60);

    if (hours > 0) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const turndownService = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced'
  });

  const convertMarkdownToHtml = (markdown) => {
    try {
      const result = remark()
        .use(remarkHtml, { sanitize: false })
        .processSync(markdown);
      return result.toString();
    } catch (error) {
      console.error('Error converting markdown to HTML:', error);
      return markdown;
    }
  };

  const convertHtmlToMarkdown = (html) => {
    try {
      return turndownService.turndown(html);
    } catch (error) {
      console.error('Error converting HTML to markdown:', error);
      return html;
    }
  };
  
  const normalize = (text) => {
    if (!text) return text;

    // Convert bullet character to markdown list marker
    let result = text.replace(/•\s*/g, "- ");

    // Remove 1-3 leading spaces before list markers (backend text-type header ends with "\n\n ")
    result = result.replace(/^ {1,3}([-*+] )/gm, "$1");

    // Collapse blank lines between consecutive list items
    result = result.replace(/(^- .+)\n\n(?=- )/gm, "$1\n");

    return result;
  };
  const htmlValue = convertMarkdownToHtml(editorVal);

  const handleUploadFirst = async () => {
    if (isRecording) {
      stop();
      return;
    }
  };

  const cannotUploadDocuments = (selectedDocIds || []).length === 0;

  const getButtonTextClass = () => {
    if (cannotUploadDocuments) return "opacity-20";
    return "opacity-100";
  };

  const { data: myDocs } = useQuery('getUserFiles', getFiles, { retry: 0 });

  const queryClient = useQueryClient();

  const { mutate: createFileMutation } = useMutation(createFile,
    {
      onSuccess: () => {
        toast.success("File uploaded.");
      },
      onError: () => {
        toast.error("That upload didn't work. Try again.");
      },
      onSettled: () => {
        queryClient.invalidateQueries('getUserFiles');
        setReuploadText('');
      }
    });

  const triggerAutoDownload = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const { isComponentVisible, toggleChild, wrapRef } = useComponentClickOutside();
  const { isComponentVisible: isLangVisible, toggleChild: toggleLangChild, wrapRef: langWrapRef } = useComponentClickOutside();

  // Multi-select language state. Default: ["en"] → nova-3-medical.
  // "auto" is mutually exclusive with specific codes. Max 2 specific languages.
  const [selectedLanguages, setSelectedLanguages] = useState(["en"]);
  const [langError, setLangError] = useState("");

  const toggleLanguage = (code) => {
    setLangError("");
    if (code === "auto") {
      // Toggle auto: if already selected → revert to English; else replace all
      if (selectedLanguages.includes("auto")) {
        setSelectedLanguages(["en"]);
      } else {
        setSelectedLanguages(["auto"]);
      }
      return;
    }
    // Specific language: drop "auto" if present, then toggle
    const without = selectedLanguages.filter(l => l !== "auto");
    if (without.includes(code)) {
      const next = without.filter(l => l !== code);
      setSelectedLanguages(next.length > 0 ? next : ["en"]);
    } else {
      if (without.length >= 2) {
        setLangError("Cannot select more than 2 languages");
        return;
      }
      setSelectedLanguages([...without, code]);
    }
  };

  const langButtonText = (() => {
    if (selectedLanguages.includes("auto")) return "Auto";
    return selectedLanguages
      .map(c => LANGUAGE_OPTIONS.find(l => l.code === c)?.shortLabel ?? c.toUpperCase())
      .join(", ");
  })();

  const handleFillDocuments = async () => {
    setReuploadText('Filling the docs...');
    try {
      const res = await fillDocuments({
        transcribedText,
        documentIds: selectedDocIds,
      })
      if (!res.allProcessed) {
        toast.error("Files were not processed")
        return;
      }
      triggerAutoDownload(res.fileUrl, res.fileName);

    } catch (error) {
      console.log(error.message);
    }
    finally {
      setReuploadText('');
    }
  }

  const uploadAudio = async () => {
    const latestTranscript = getLatestTranscript();
    const plainTranscript = stripTimestampMarkers(latestTranscript);
    const wordCount = plainTranscript.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < 30) {
      setNotesBlockReason("short_transcript");
      setShowCoreBox(true);
      return;
    }
    setShortTranscriptWarning(wordCount < 150);

    try {
      setLoading(true);
      setIsGeneratingNotes(true);

      const payload = {
        transcribedText: plainTranscript,
        selectedTemplates: selectedTemplates ?? [],
        sessionId: currentSession?.id,
      };

      const response = await getNotesWithMoreTemplates(payload);

      const responseGeneratedNotes = response.outputs.map(note => ({
        ...note,
        output: normalize(note.output),
      }));
      setNotesBlockReason(null);
      setGeneratedNotes(responseGeneratedNotes);
      setTranscribedText(latestTranscript);
      typedNotesRef.current = new Set();
      setCurrentAudioBlob(audioBlob);
      setChangesMade(true);
      setRecommendation('');
      typedRecommendationRef.current = new Set();
    } catch (error) {
      console.error("Error:", error);
      GenericError();
    } finally {
      setShowCoreBox(true);
      setLoading(false);
      setIsGeneratingNotes(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (notesRef.current && !notesRef.current.contains(event.target)) {
        if (isEditingNote && !isRecording) {
          const tempGeneratedNotes = [...generatedNotes];
          const index = tempGeneratedNotes.findIndex(
            (el) => parseInt(el.templateId) === parseInt(active)
          );
          if (index >= 0) {
            tempGeneratedNotes[index] = {
              ...tempGeneratedNotes[index],
              output: isNoteEdited ? convertHtmlToMarkdown(editorVal) : editorVal,
            };
            setGeneratedNotes([...tempGeneratedNotes]);
            setChangesMade(true);
          }
        }
        setIsEditingNote(false);
        setIsNoteEdited(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [generatedNotes, editorVal, active, setGeneratedNotes]);

  const { data: favorites } = useQuery('getAllFavorites', getAllFavoriteTemplates);

  useEffect(() => {
    if (!currentSession && favorites && favorites.length > 0) {
      // Patient Info Letter (id=4) is always secondary — sort it last regardless
      // of the order the backend returns (Prisma returns by composite PK = templateId asc).
      const ordered = [...favorites].sort((a, b) => {
        if (Number(a.templateId) === 4) return 1;
        if (Number(b.templateId) === 4) return -1;
        return 0;
      });
      setSelectedTemplates(applySavedTemplateOrder(ordered.map(fav => Number(fav.templateId))));
    }
    else if (favorites && favorites.length === 0 && !currentSession) {
      setSelectedTemplates(DEFAULT_TEMPLATE_IDs)
    }
  }, [favorites, currentSession])

  /* Drag a tab onto another and it takes that position. The order is saved
     locally, not to the server: /api/favorite has no order column (Prisma
     returns them by composite primary key), and this repo is frontend-only, so
     persisting it properly is a backend change. Local storage means the order a
     clinician sets survives a reload on the machine they set it on, which is
     the case that actually matters. Raised for the backend. */
  const handleReorderTemplates = (fromId, toId) => {
    const current = selectedTemplates || [];
    const from = current.indexOf(fromId);
    const to = current.indexOf(toId);
    if (from === -1 || to === -1 || from === to) return;

    const next = current.filter((id) => id !== fromId);
    next.splice(to, 0, fromId);

    setSelectedTemplates(next);
    saveTemplateOrder(next);
  };

  /* The X on a tab drops the template from favourites outright — the tab strip
     IS the favourites list, so hiding it here while leaving it favourited would
     put the two out of step. No toast: the tab vanishing is the confirmation,
     and the owner asked for silence.

     At least one template always survives. PanelTabs already hides the control
     on the last tab; this is the second guard, because the API call is the part
     that cannot be taken back. */
  const handleRemoveTemplate = async (templateId) => {
    const current = selectedTemplates || [];
    if (current.length <= 1) return;

    const next = current.filter((id) => id !== templateId);
    setSelectedTemplates(next);
    saveTemplateOrder(next);
    if (active === templateId) setActive(next[0]);

    try {
      await deleteFavoriteTemplate({ templateId });
      queryClient.invalidateQueries('getAllFavorites');
      queryClient.invalidateQueries(['templates', userId]);
      queryClient.invalidateQueries('customTemplates');
    } catch (error) {
      // Put it back rather than leaving the strip disagreeing with the server.
      setSelectedTemplates(current);
      saveTemplateOrder(current);
      toast.error(errorCopy(error?.response?.data?.error || "Could not remove that template."));
    }
  };

  const activeTemplateName =
    (allTemplates || []).find(t => t.id === active)?.name ||
    (customTemplatesData || []).find(t => t.id === active)?.name ||
    "template";

  /* The band's running commentary. Three states, in the order a consult moves
     through them, each in the brand's voice: a plain report, one dry beat, no
     exclamation. Rendered under the Start listening button — the control that
     causes each transition — rather than in the notes box, which is busy showing
     the note itself.

     Named statusLine, not sessionStatus: the store already owns that name for
     the persisted 'in_progress' flag, and shadowing it here would be a trap for
     the next person. */
  const statusLine = (() => {
    if (isRecording) {
      return (
        <>
          <span className="relative flex h-2 w-2 flex-shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warn opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-warn" />
          </span>
          <p className="font-body text-meta font-medium text-warn">Listening. Talk normally.</p>
        </>
      );
    }

    if (loading) {
      // The brand's one waiting motion, at the size the band can carry.
      return <NsLoader size="sm" bare label="Writing it up" />;
    }

    if ((generatedNotes || []).length > 0) {
      return (
        <>
          <CheckCircle2 size={13} className="flex-shrink-0 text-accent" />
          <p className="font-body text-meta font-medium text-accent">Written up. Have a read.</p>
        </>
      );
    }

    return null;
  })();

  /* The two boxes fill whatever is left of the viewport instead of taking a
     fixed 65vh, which left a dead band across the bottom of the screen.

     Measured rather than `calc(100vh - <constant>)`, because the distance from
     the top of the page to these boxes is not constant: the resume banner, the
     resuming banner and the mobile header all move them. Below xl the boxes
     stack and Tailwind's min-height governs. */
  const panelsRef = useRef(null);
  const [panelsHeight, setPanelsHeight] = useState(null);

  const measurePanels = useCallback(() => {
    const el = panelsRef.current;
    // A collapsed or hidden pane reports innerWidth 0, and every measurement
    // taken from it is a lie — that has cost hours on this screen before.
    if (!el || !window.innerWidth || window.innerWidth < 1280) {
      setPanelsHeight((current) => (current === null ? current : null));
      return;
    }
    // Document offset, not viewport offset: rect.top alone moves with scroll,
    // and the boxes would grow and shrink as the page scrolled.
    const offsetTop = el.getBoundingClientRect().top + window.scrollY;
    const next = Math.max(
      PANELS_MIN_HEIGHT,
      window.innerHeight - offsetTop - PANELS_BOTTOM_RESERVE
    );
    setPanelsHeight((current) => (current === next ? current : next));
  }, []);

  /* Re-measure after every render, deliberately.

     The banners above these boxes mount and unmount, which moves them down the
     page without changing the page's height at all — the content wrapper is
     min-h-screen, so a ResizeObserver on the body never fires and the boxes kept
     the height they were given when the resume banner was still on screen. Every
     one of those moves is a React render, so this is the signal that actually
     fires. The equality guard in measurePanels stops it re-triggering itself. */
  useEffect(() => {
    measurePanels();
  });

  useEffect(() => {
    window.addEventListener("resize", measurePanels);
    // Web fonts reflow everything above the boxes when they land.
    document.fonts?.ready.then(measurePanels);
    return () => window.removeEventListener("resize", measurePanels);
  }, [measurePanels]);

  // One card per selected template, name resolved from either catalogue.
  const templateCards = (selectedTemplates || []).map((templateId) => {
    const template =
      (allTemplates || []).find((el) => el.id === templateId) ||
      (customTemplatesData || []).find((temp) => temp.id === templateId);
    return { id: templateId, name: template?.name || `Template ${templateId}` };
  });

  if (templatesError) {
    return (
      <div className="w-full flex justify-center items-center min-h-[400px] flex-col">
        <p className="text-danger text-base mb-4">Error loading templates</p>
        <button
          onClick={() => refetchTemplates()}
          className="bg-accent text-white px-6 py-3 rounded-ns font-semibold transition-all duration-200 hover:shadow-pop"
        >
          Retry Loading Templates
        </button>
      </div>
    );
  }

  // ✅ REPLACE THIS SECTION - Show upgrade modal instead of just message
  if (isFreeTrialSessionExhausted || isUserProTrialInactive) {
      return (
        <>
          <div className="bg-danger/10 border border-danger py-4 px-6 w-full mb-4 rounded-ns">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-danger mb-1">
                  {isFreeTrialSessionExhausted ? "Free Trial Limit Reached" : "Subscription Required"}
                </h3>
                <p className="text-sm text-danger mb-3">
                  {isFreeTrialSessionExhausted &&
                    "You've reached your monthly limit of 15 sessions. Upgrade to Pro for unlimited sessions or wait until your trial resets."}
                  {isUserProTrialInactive &&
                    "Your subscription is inactive. Please upgrade to a Pro plan to continue creating sessions."}
                </p>
                <ChoosePlan />
              </div>
            </div>
          </div>
        </>
      );
    }

  if (userDataLoading) {
    return (<div className="h-full w-full flex items-center justify-center">
      <Spinner size={30} />
    </div>)
  }

  return (
    <>
      {/* New Session Ready toast */}
      {showNewSessionReady && (
        <div className="fixed bottom-8 left-0 right-0 flex justify-center z-50 pointer-events-none">
          <div className="flex items-center gap-2.5 bg-ink text-white px-5 py-3 rounded-ns-tile shadow-pop animate-fade-up">
            <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0" />
            <span className="text-sm font-semibold tracking-wide">New Session Ready</span>
          </div>
        </div>
      )}

      {/* History-continue resuming banner */}
      {showResumingBanner && (
        <div className="mb-4 flex items-start gap-3 rounded-ns border border-rule bg-shade-50 px-4 py-3 ">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-deep">Resuming existing session</p>
            <p className="text-xs text-accent mt-0.5">
              {resumingPatientName && resumingPatientName !== 'Untitled'
                ? `Continuing session for ${resumingPatientName}`
                : 'Continuing your previous session'}
            </p>
          </div>
          <button
            onClick={() => setShowResumingBanner(false)}
            className="flex-shrink-0 rounded-ns border border-rule px-3 py-1.5 text-xs font-semibold text-accent hover:bg-shade-50 active:scale-95 transition-all"
          >
            OK
          </button>
        </div>
      )}

      {/* Crash-recovery resume banner */}
      {resumeBanner && (
        <div className="mb-4 flex items-start gap-3 rounded-ns border border-warn bg-warn/10 px-4 py-3 ">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-warn" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-warn">Unfinished session detected</p>
            <p className="text-xs text-warn mt-0.5">
              You have a paused session
              {resumeBanner.patientName && resumeBanner.patientName !== 'Untitled'
                ? ` for ${resumeBanner.patientName}`
                : ''}
              {resumeBanner.timerTick > 0
                ? ` (${Math.floor(resumeBanner.timerTick / 60)}m ${resumeBanner.timerTick % 60}s)`
                : ''}
              . Resume where you left off?
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={handleResumeSession}
              className="rounded-ns bg-warn px-3 py-1.5 text-xs font-semibold text-white active:scale-95 transition-all"
            >
              Resume
            </button>
            <button
              onClick={handleDismissResume}
              className="rounded-ns border border-warn px-3 py-1.5 text-xs font-semibold text-warn hover:bg-warn/10 active:scale-95 transition-all"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="w-full">
        {/* The session controls, grouped on the one tinted band on the page.
            accent-tint is used here as a ground, which is the single thing
            standards §4 allows it to be — never type, never a card fill.

            The "1 — Start here" cue that used to head this band is gone at the
            owner's call. What replaced it is better: the band now reports what
            the app is actually doing, under the button that does it. */}
        <div className="mb-4 rounded-ns border border-solid border-accent/15 bg-accent-tint px-3 py-2">

          {/* pb-5 reserves the status line's row on the FLEX ROW, not on the
              right-hand column. On the column it shrank that column's content
              box and `items-center` then centred it 8px higher than the patient
              field beside it — the misalignment moved rather than went away. */}
          <div className="relative flex flex-col gap-3 pb-5 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
            {/* Row 1 on mobile / Left on desktop: patient name + language + timer (mobile only) */}
            <div className="flex items-center gap-3 w-full lg:w-auto">
              <div className="flex-1 min-w-0">
                <SearchablePatientDropdown
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  fullPatientDetails={currentPatient}
                  patientName={currentPatientName}
                  getAllPatients={getAllPatients}
                />
              </div>
              {/* Language selector. Disabled while recording rather than unmounted:
                  taking it out of the row re-flowed everything beside it the
                  instant recording started, which is half of why the controls
                  stopped lining up. The language cannot be changed mid-recording
                  either way. */}
              <div ref={langWrapRef} className="relative flex-shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleLangChild(); }}
                    disabled={createSessionLoading || loading || isRecording}
                    className="bg-white text-ink text-sm font-semibold px-4 h-[38px] rounded-ns border border-rule-strong transition-all duration-200 flex items-center gap-2 active:scale-95 hover:bg-shade-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Input language"
                  >
                    <Languages size={15} className="flex-shrink-0" />
                    <span>{langButtonText}</span>
                    <ChevronDown strokeWidth={2.5} size={14} />
                  </button>

                  {isLangVisible && (
                    <div className="absolute top-full mt-2 left-0 bg-white rounded-ns shadow-pop border-2 border-ink z-50 animate-slideDown overflow-hidden w-[210px] max-w-[calc(100vw-2rem)]">
                      <div className="px-3 pt-2.5 pb-1 text-[10px] font-semibold text-muted uppercase tracking-wider">
                        Input Language <span className="font-normal normal-case">(max 2)</span>
                      </div>
                      <div
                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-shade-50 transition-colors duration-150 border-b border-rule"
                        onClick={(e) => { e.stopPropagation(); toggleLanguage("auto"); }}
                      >
                        <span className="flex-1 text-xs font-medium text-muted">Auto-detect</span>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          selectedLanguages.includes("auto") ? "bg-shade-50 border-accent" : "border-rule-strong"
                        }`}>
                          {selectedLanguages.includes("auto") && <Check size={10} strokeWidth={3} className="text-ink" />}
                        </div>
                      </div>
                      {langError && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-danger/10 border-b border-danger text-[11px] text-danger font-medium">
                          <AlertTriangle size={11} className="flex-shrink-0" />
                          {langError}
                        </div>
                      )}
                      <div className="max-h-52 overflow-y-auto">
                        {LANGUAGE_OPTIONS.filter(l => l.code !== "auto").map((lang) => {
                          const isChecked = selectedLanguages.includes(lang.code);
                          const isAutoActive = selectedLanguages.includes("auto");
                          return (
                            <div
                              key={lang.code}
                              className={`flex items-center gap-2.5 px-3 py-2 text-xs transition-colors duration-150 ${
                                isAutoActive
                                  ? "opacity-40 cursor-not-allowed"
                                  : "cursor-pointer text-muted hover:bg-shade-50"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isAutoActive) toggleLanguage(lang.code);
                              }}
                            >
                              <span className="flex-1 font-medium">{lang.label}</span>
                              <span className="text-[10px] text-muted font-mono uppercase">{lang.shortLabel}</span>
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                isChecked && !isAutoActive ? "bg-shade-50 border-accent" : "border-rule-strong"
                              }`}>
                                {isChecked && !isAutoActive && <Check size={10} strokeWidth={3} className="text-ink" />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2 border-t border-rule bg-shade-50">
                        <Lock size={10} className="text-muted flex-shrink-0" />
                        <span className="text-[10px] text-muted">Output language:</span>
                        <span className="text-[10px] font-semibold text-muted">English</span>
                      </div>
                    </div>
                  )}
              </div>
              <span className="lg:hidden self-center text-sm font-mono tabular-nums font-semibold text-accent min-w-[3.2rem] text-right leading-none flex-shrink-0">
                {formatDuration(timerTick)}
              </span>
            </div>

            {/* Row 2 on mobile / Right on desktop: waveform + start/stop + next session,
                with the session's own running commentary underneath it.

                The status line is absolutely positioned and its space is reserved
                by the row's pb-5, both deliberately. In flow it was the widest thing in this
                column, so on `justify-between` it dragged the whole control group
                — the green button included — sideways the moment recording
                started, and its height pushed the row's vertical centre so the
                patient field and the button stopped lining up. Out of flow, with
                its height always reserved, nothing moves between the three
                states. */}
            <div className="flex flex-col items-stretch lg:items-end">
            <div className="flex items-center gap-3 flex-nowrap w-full lg:w-auto justify-end lg:justify-start">
              {/* Waveform. The slot keeps its width whether or not it is drawing:
                  an animating width was moving the record button out from under
                  the cursor at the exact moment the clinician pressed it. Only
                  the opacity changes now. No width/height attributes — the hook
                  sizes the backing store to devicePixelRatio every frame. */}
              <div
                className={`${showCoreBox ? 'hidden sm:block' : ''} h-[52px] w-[110px] flex-shrink-0 overflow-hidden sm:w-[190px] transition-opacity duration-300 ease-ui ${
                  isRecording && !isPaused ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <canvas ref={canvasRef} aria-hidden="true" className="block h-full w-full" />
              </div>

              {/* Timer — desktop only, always visible */}
              <span className="hidden lg:inline text-sm font-mono tabular-nums font-semibold text-accent min-w-[3.2rem] text-right leading-none">
                {formatDuration(timerTick)}
              </span>


              {/* Start/Stop/Resume button + mode selector — unified split button */}
              <div ref={wrapRef} className="relative">
                <div className="flex rounded-ns border border-ink  hover:shadow-pop transition-shadow duration-200 overflow-hidden">
                  {/* Main action part */}
                  <button
                    onClick={async () => {
                      if (loading) return;
                      if (!currentSession) {
                        if (modeCliked === 1) {
                          await setUpTelehealthMode(getCognitoIdToken());
                          if (teleConsent) {
                            await handleSubmit(handleSessionCreation)();
                          }
                        } else {
                          await handleSubmit(handleSessionCreation)();
                        }
                      } else if (!showCoreBox) {
                        await handleUploadFirst();
                      } else {
                        !isRecording ? start() : stop();
                      }
                    }}
                    /* Recording is `warn`, not `danger`. Stopping a recording is
                       not destructive — nothing is lost by it — and red sitting
                       beside the genuinely destructive Next Session button
                       flattened the difference between the two. */
                    className={`text-sm font-semibold px-4 py-2 transition-colors duration-ui ease-ui flex items-center justify-center gap-2 active:scale-[.98] ${
                      isRecording
                        ? 'bg-warn text-white hover:bg-warn/90'
                        : 'bg-accent text-white hover:bg-deep'
                    }`}
                    disabled={createSessionLoading || loading || retryingTemplateId !== null}
                  >
                    {createSessionLoading ? (
                      <span className="text-white">Starting…</span>
                    ) : (
                      <>
                        {modeCliked !== 1 ? (
                          <Mic className="h-5 w-5" />
                        ) : (
                          <SquareUser className="h-5 w-5" />
                        )}
                        <span>
                          {!currentSession && !isRecording
                            ? 'Start listening'
                            : isRecording
                            ? 'Stop listening'
                            : 'Resume'}
                        </span>
                      </>
                    )}
                  </button>

                  {/* Divider + chevron — only when not actively recording */}
                  {(!currentSession || (currentSession && !isRecording)) && (
                    <>
                      <div className="w-px bg-ink self-stretch" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleChild();
                        }}
                        className="bg-shade-50 text-ink px-2.5 transition-colors duration-200 active:scale-95 flex items-center disabled:opacity-50"
                        disabled={createSessionLoading || loading || retryingTemplateId !== null}
                      >
                        <ChevronDown strokeWidth={2.5} size={16} />
                      </button>
                    </>
                  )}
                </div>

                {/* Mode selection dropdown */}
                {isComponentVisible && (!currentSession || (currentSession && !isRecording)) && (
                  <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-ns shadow-pop border-2 border-ink py-1 z-10 animate-slideDown overflow-hidden min-w-[200px]">
                    {MODES.map((mode, ind) => (
                      <div
                        key={ind}
                        className={`gap-2 items-center text-xs px-3 py-2.5 cursor-pointer text-muted hover:bg-shade-50 transition-colors duration-150 ${ind === 1 ? 'hidden sm:flex' : 'flex'}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (currentSession && modeCliked !== ind) {
                            await cleanupAudioResources();
                          }
                          setModeCliked(ind);
                          toggleChild();
                        }}
                      >
                        <span className="text-accent flex-shrink-0">{mode.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{mode.title}</div>
                        </div>
                        {ind === modeCliked && (
                          <Check size={16} className="text-accent flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Next Patient button */}
              {showCoreBox && (
                <button
                  onClick={handleNextPatientClick}
                  disabled={savingSession || isRecording || retryingTemplateId !== null}
                  className="relative flex items-center justify-center gap-2 px-4 py-2 sm:px-6 text-sm font-semibold text-white bg-danger rounded-ns border border-danger  hover:shadow-pop transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <span className={`text-white ${savingSession && !isNotesAutoSaving ? "opacity-0" : "opacity-100"}`}>
                    <NextIcon width={8}/>
                  </span>
                  <span className={savingSession && !isNotesAutoSaving ? "opacity-0" : "opacity-100"}>
                    Next Session
                  </span>
                  {savingSession && !isNotesAutoSaving && (
                    <ScaleLoader loading color="white" className="absolute" height={15} />
                  )}
                </button>
              )}
            </div>

            {/* One line, under the button that caused it. Recording, writing up,
                and written — the three states a clinician needs to be able to
                read at a glance from across the room. */}
            <div
              className="absolute bottom-0 right-0 flex h-5 items-center justify-end gap-2 whitespace-nowrap"
              role="status"
              aria-live="polite"
            >
              {statusLine}
            </div>
            </div>
          </div>
        </div>

        <div>
          {/* Mobile: one box at a time. Labelled with the two panel titles —
              Session and Notes — not "Transcript" and "Notes": Transcript is a
              tab *inside* Session, and having the word head two stacked rows
              that mean different things read as a bug. */}
          <div className="sm:hidden mb-3 flex overflow-hidden rounded-ns border border-solid border-rule">
            <button
              type="button"
              onClick={() => setMobilePanelView('transcript')}
              className={`flex-1 py-2 font-body text-ui-sm transition-colors duration-ui ease-ui ${
                mobilePanelView === 'transcript' ? 'bg-accent-tint font-medium text-accent' : 'bg-white text-muted'
              }`}
            >
              Session
            </button>
            <button
              type="button"
              onClick={() => setMobilePanelView('notes')}
              className={`flex-1 py-2 font-body text-ui-sm transition-colors duration-ui ease-ui ${
                mobilePanelView === 'notes' ? 'bg-accent-tint font-medium text-accent' : 'bg-white text-muted'
              }`}
            >
              Notes
            </button>
          </div>

          {/* Two boxes. The tab row is gone: templates are no longer a row of
              tabs above the notes — every selected template gets its own card in
              the right-hand box, so all notes are visible at once. */}
          <div
            ref={panelsRef}
            style={panelsHeight ? { height: `${panelsHeight}px` } : undefined}
            /* mb-20 below xl: the page scrolls there and the floating Feedback
               button is fixed to the viewport corner, so without the extra room
               it sits permanently on top of the notes footer actions. At xl the
               boxes already stop short of it by design. */
            className="mb-20 flex flex-col gap-6 xl:mb-8 xl:flex-row"
          >
            {/* LEFT — transcript, with Insights as its own control in the header */}
            <div
              className={`flex min-h-[420px] flex-col xl:h-full ${
                mobilePanelView !== 'transcript' ? 'hidden sm:flex' : 'flex'
              } xl:w-1/2`}
            >
              {/* Title, hairline, tabs — the same shape as the Notes header, and
                  h-12 is shared with it so the two rules, and therefore the two
                  boxes below them, line up. Change one and change the other.

                  Insights used to be a lit icon button that swapped the panel's
                  contents with no way back on screen. Two tabs make both states
                  visible and either one reachable in a single click. */}
              <div className="flex h-12 flex-shrink-0 items-stretch gap-3 border-b border-solid border-rule">
                <div className="flex flex-shrink-0 items-center">
                  <h2 className="font-display text-ui-md font-normal text-ink">Session</h2>
                </div>

                <div className="my-3 w-px flex-shrink-0 bg-rule-strong" />

                <PanelTabs
                  tabs={[
                    { id: 'transcript', label: 'Transcript' },
                    { id: 'insights', label: 'Insights' },
                  ]}
                  activeId={showRecommendation ? 'insights' : 'transcript'}
                  onSelect={(id) => (id === 'insights' ? showInsights() : setShowRecommendation(false))}
                  ariaLabel="Session view"
                  className="flex-1"
                />

                {isRecording && (
                  <div className="flex flex-shrink-0 items-center gap-2 self-center">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warn opacity-60" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-warn" />
                    </span>
                    <span className="font-body text-ui-xs font-medium text-warn">Live</span>
                  </div>
                )}
              </div>

              {/* Flex column, not a percentage height: the box below sizes itself
                  with flex-1. h-full would resolve against a parent whose own
                  height is flex-derived, which collapses below xl. */}
              <div className="flex min-h-0 flex-1 flex-col pt-3">
                <TranscriptContainer
                  isVisible
                  className="w-full"
                  isRecording={isRecording}
                  showRecommendation={showRecommendation}
                  shortTranscriptWarning={shortTranscriptWarning}
                  setIsTranscriptEdited={setIsTranscriptEdited}
                  transcriptScrollRef={transcriptScrollRef}
                  editableTranscriptionRef={editableTranscriptionRef}
                  transcribedText={transcribedText}
                  recommendationLoading={recommendationLoading}
                  recommendation={recommendation}
                  isTranscriptEdited={isTranscriptEdited}
                  createSessionLoading={createSessionLoading}
                  loading={loading}
                  handleTranscriptUpdate={handleTranscriptUpdate}
                  Spinner={Spinner}
                  WarnIcon={WarnIcon}
                  autoScrollEnabledRef={autoScrollEnabledRef}
                  setShowScrollButton={setShowScrollButton}
                  showScrollButton={showScrollButton}
                  TypewriterMarkdown={TypewriterMarkdown}
                  typedRecommendationRef={typedRecommendationRef}
                  liveUtterances={liveUtterances}
                  onUtteranceTextChange={handleUtteranceTextChange}
                />
              </div>
            </div>

            {/* RIGHT — every template, stacked, with Add more in the header */}
            <NotesPanel
              className={`min-h-[420px] xl:h-full xl:w-1/2 ${
                mobilePanelView !== 'notes' ? 'hidden sm:flex' : 'flex'
              }`}
              templates={templateCards}
              generatedNotes={generatedNotes}
              active={active}
              setActive={setActive}
              isEditingNote={isEditingNote}
              setIsEditingNote={setIsEditingNote}
              setEditorVal={setEditorVal}
              setIsNoteEdited={setIsNoteEdited}
              htmlValue={htmlValue}
              notesRef={notesRef}
              loading={loading}
              notesBlockReason={notesBlockReason}
              retryingTemplateId={retryingTemplateId}
              isRecording={isRecording}
              isCopied={isCopied}
              copyToClipboard={copyToClipboard}
              handleDictate={handleDictate}
              handleRetryTemplate={handleRetryTemplate}
              TypewriterMarkdown={TypewriterMarkdown}
              typedNotesRef={typedNotesRef}
              onReorderTemplates={handleReorderTemplates}
              onRemoveTemplate={handleRemoveTemplate}
              /* The refine composer used to sit in a bar under BOTH boxes, which
                 cost a full row of the page and read as page furniture rather
                 than as something belonging to the note. It lives in the notes
                 box footer now, behind the comment icon; NotesPanel owns the
                 open/closed state, this file keeps every handler. */
              refineSlot={
                currentNote?.length > 0 ? (
                  <div className="flex items-center gap-2">
                    <textarea
                      value={noteChangesInput}
                      onChange={handleNoteChangesInput}
                      onKeyDown={handleRegenerateNotesOnPressEnterKey}
                      className="min-w-0 flex-1 resize-none rounded-ns border border-solid border-rule-strong bg-white px-3 py-2 font-body text-ui-xs text-ink outline-none transition-colors duration-ui ease-ui focus:border-accent focus:ring-1 focus:ring-accent"
                      placeholder="Tell Nirva what you need…"
                      rows={1}
                    />
                    <button
                      type="button"
                      onClick={() => !regeneratingNotes && handleRegeneratedNotes()}
                      disabled={regeneratingNotes || noteChangesInput.toString().trim().length === 0}
                      className="flex h-9 flex-shrink-0 items-center gap-1.5 rounded-ns bg-accent px-3 font-body text-ui-xs font-semibold text-white transition-colors duration-ui ease-ui hover:bg-deep active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {regeneratingNotes ? (
                        <ScaleLoader loading color="white" height={12} width={2} />
                      ) : (
                        <>
                          <SendAiIcon />
                          <span>Send</span>
                        </>
                      )}
                    </button>
                  </div>
                ) : null
              }
              addMoreSlot={
                <AddMoreTemplates
                  setActive={setActive}
                  allTemplates={allTemplates || []}
                  setChangesMade={setChangesMade}
                  setIsTranscriptEdited={setIsTranscriptEdited}
                  setLoadingTemplates={setLoading}
                />
              }
            />
          </div>

        </div>
      </div>

      {regeneratedNotes && (
        <Modal
          isOpen={!!regeneratedNotes}
          closeModal={() => setRegeneratedNotes(null)}
        >
          <RegeneratedNotes
            templateId={active}
            setChangesMade={setChangesMade}
            prevNotes={regeneratedNotes?.prevOutput}
            setNoteChangesInput={setNoteChangesInput}
            setRegeneratedNotes={setRegeneratedNotes}
            regeneratedNotes={regeneratedNotes?.newOutput}
            setIsNotesAutoSaving={setIsNotesAutoSaving}
            handleSaveSession={handleSaveSession}
          />
        </Modal>
      )}

      {showChooseDocumentsModal && (
        <>
          {ReactDOM.createPortal(
            <div className="fixed inset-0 w-screen h-screen bg-ink/60 backdrop-blur-sm z-20 animate-fadeIn" />,
            document.body
          )}

          {ReactDOM.createPortal(
            <div
              style={{ transform: "translate(-50%, -50%)" }}
              className="fixed left-1/2 top-1/2 z-[70] w-[90vw] sm:w-[70vw] max-w-[700px] bg-white rounded-ns border border-rule-strong shadow-pop flex flex-col p-6 animate-scaleIn"
            >
              <div className="flex items-center w-full justify-between mb-4 pb-4 border-b border-rule">
                <h2 className="font-bold text-xl text-ink">
                  Choose Documents<span className="text-danger">*</span>
                </h2>
                <CloseButton onClick={() => setShowChooseDocumentsModal(false)} />
              </div>

              <div className="border border-rule-strong bg-shade-50 w-full p-4 rounded-ns flex flex-col flex-1 overflow-hidden">
                <div className="flex justify-between items-center border-b border-rule-strong pb-3 mb-4">
                  <h3 className="font-semibold text-lg text-ink">My Documents</h3>
                  <label
                    htmlFor="file-upload"
                    className="flex bg-ink rounded-ns px-4 py-2 cursor-pointer items-center gap-2 transition-all duration-200  active:scale-95"
                  >
                    <Upload className="text-white" size={18} />
                    <span className="text-white font-semibold text-sm">Upload</span>
                  </label>
                  <input
                    id="file-upload"
                    accept={process.env.REACT_APP_ACCEPT_DOCs}
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > process.env.REACT_APP_MAX_FILE_SIZE_IN_MB * 1024 * 1024) {
                        toast.error("File size must be under 10 MB");
                        return;
                      }
                      setReuploadText("File Uploading...");
                      createFileMutation(file);
                    }}
                  />
                </div>

                <div className="overflow-y-auto flex-1 bg-white rounded-ns border border-rule">
                  {myDocs?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted">
                      <NotepadText size={48} className="mb-4 opacity-50" />
                      <p className="text-sm">No documents found</p>
                    </div>
                  ) : (
                    <ul>
                      {myDocs?.map((doc) => <DocItem key={doc.id} doc={doc} />)}
                    </ul>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={handleFillDocuments}
                disabled={cannotUploadDocuments}
                className="mt-6 w-full md:w-auto mx-auto text-white font-semibold px-8 py-3 flex items-center justify-center gap-2 bg-accent rounded-ns  hover:shadow-pop transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <span className={getButtonTextClass()}>
                  <StarsIcon />
                </span>
                <span className={getButtonTextClass()}>Fill documents</span>
              </button>
            </div>,
            document.body
          )}
        </>
      )}

      {showDeleteFileModal && (
        <Modal
          isOpen={showDeleteFileModal}
          closeModal={() => setShowDeleteFileModal(false)}
        >
          <DeleteFileConfirmation
            data={showDeleteFileModal}
            setShowDeleteFileModal={setShowDeleteFileModal}
          />
        </Modal>
      )}

      {reUploadText && <ScaleBarrierLoader text={reUploadText} />}
    </>
  );
};

export default SaveSession;